import { type AudienceInput, withAudience } from './audience';
import { trackChanges } from './scheduleNotices';
import { normalize } from './dates';
import { type EditOptions, applyEventEdit } from './eventEdit';
import { changeSeriesEnd } from './eventSeries';
import type { GenerationResult } from './generator';
import type { ImportRow } from './csvImport';
import { keepLocation } from './geography';
import { EMPTY_PROFILE } from './members';
import { stillAhead } from './membership';
import { QUICK_BLACKOUT_REASON, syncTracking } from './presence';
import { type RecurrenceRule, recurrenceLabel, seriesDates } from './recurrence';
import { NO_ROLE_RULES } from './roleRules';
import type { Lookup } from './schedule';
import { isOpenSwap, swapApprovalError } from './swaps';
import { applyMerge } from './registrationMerge';
import { memberFromRegistration } from './registrations';
import type {
  Attendance,
  AvailState,
  DomainState,
  Member,
  Ministry,
  MinistryIconKey,
  Registration,
  RegistrationData,
  RegistrationLink,
  Role,
  ResponseStatus,
  ScheduleResponse,
  ScheduleEvent,
  Sex,
  SwapRequest,
  MergeField,
} from './types';

/**
 * Transições do estado — funções puras, sem React. A store (`AppStore.tsx`) só as
 * compõe com desfazer, histórico e aviso; aqui mora a regra de cada mudança, e é aqui
 * que os testes a exercitam.
 */

const byDateTime = (a: ScheduleEvent, b: ScheduleEvent) => (a.date + a.time).localeCompare(b.date + b.time);

/**
 * Quem entra numa função preenche a vaga reaberta: cada pessoa nova consome a recusa mais
 * antiga daquela função (o registro da recusa fica no histórico do evento).
 */
export function fillDeclines(before: ScheduleEvent, after: ScheduleEvent): ScheduleEvent {
  const responses = { ...after.responses };
  let changed = false;

  for (const [roleId, ids] of Object.entries(after.slots)) {
    const was = new Set(before.slots[roleId] ?? []);
    let added = ids.filter((id) => !was.has(id)).length;
    const open = Object.entries(responses)
      .filter(([, r]) => r.status === 'declined' && r.roleId === roleId)
      .sort(([, a], [, b]) => a.at - b.at);

    for (const [memberId] of open) {
      if (added <= 0) break;
      delete responses[memberId];
      added--;
      changed = true;
    }
  }

  return changed ? { ...after, responses } : after;
}

/** Edição manual da escala: marca o evento como gerado (tem escala) e, se já estava publicado, continua publicado. */
function editEvent(
  events: ScheduleEvent[],
  eventId: string,
  change: (slots: Record<string, string[]>) => Record<string, string[]>
): ScheduleEvent[] {
  // A publicada continua publicada: só quem mudou fica sabendo (`trackChanges`).
  return events.map((e) =>
    e.id === eventId
      ? trackChanges(
          e,
          fillDeclines(e, syncTracking({ ...e, slots: change({ ...e.slots }), generated: true }))
        )
      : e
  );
}

export function assignMember(
  events: ScheduleEvent[],
  eventId: string,
  roleId: string,
  memberId: string,
  replaceId?: string | null
): ScheduleEvent[] {
  return editEvent(events, eventId, (slots) => {
    const ids = [...(slots[roleId] ?? [])];
    const at = replaceId ? ids.indexOf(replaceId) : -1;

    if (at >= 0) ids[at] = memberId;
    else ids.push(memberId);

    return { ...slots, [roleId]: ids };
  });
}

export function removeMember(events: ScheduleEvent[], eventId: string, roleId: string, memberId: string) {
  return editEvent(events, eventId, (slots) => ({
    ...slots,
    [roleId]: (slots[roleId] ?? []).filter((id) => id !== memberId),
  }));
}

export function moveMember(
  events: ScheduleEvent[],
  eventId: string,
  fromRoleId: string,
  toRoleId: string,
  memberId: string
) {
  return editEvent(events, eventId, (slots) => ({
    ...slots,
    [fromRoleId]: (slots[fromRoleId] ?? []).filter((id) => id !== memberId),
    [toRoleId]: [...(slots[toRoleId] ?? []), memberId],
  }));
}

export function publishEvent(events: ScheduleEvent[], eventId: string): ScheduleEvent[] {
  return events.map((e) => (e.id === eventId ? { ...e, published: true, generated: true } : e));
}

export function applyGeneration(
  events: ScheduleEvent[],
  result: GenerationResult,
  publish: boolean
): ScheduleEvent[] {
  const generated = new Map(
    result.events.filter((e) => result.eventIds.includes(e.id)).map((e) => [e.id, e])
  );

  return events.map((e) => {
    const next = generated.get(e.id);

    return next ? trackChanges(e, fillDeclines(e, syncTracking({ ...next, published: publish }))) : e;
  });
}

/**
 * Até onde vale a edição ou a exclusão numa série (como no Google e no iOS): só esta data,
 * esta e as próximas (as datas da série a partir desta) ou a série toda.
 */
export type EventScope = 'one' | 'following' | 'series';

export interface EventDraft {
  mode: 'create' | 'edit';
  eventId: string | null;
  seriesId: string | null;
  isRecurring: boolean;
  /** Na edição de série: só esta data, esta e as próximas ou a série toda. */
  scope: EventScope;
  /**
   * Na criação: a repetição (`null` = não se repete). A `start` da regra é a data do
   * rascunho; as datas saem de `seriesDates`.
   */
  recurrence: RecurrenceRule | null;
  /**
   * Na edição de série (esta e as próximas, ou toda): a nova data de término. Estende pela
   * regra ou encurta, tirando as datas que passam dela (`changeSeriesEnd`). Sem ela, o
   * término fica como está.
   */
  until?: string | null;
  title: string;
  /**
   * A cor escolhida (`#RRGGBB`) ou `null` para a automática da série. Na edição, ausente
   * mantém a de cada data.
   */
  color?: string | null;
  date: string;
  time: string;
  ministryIds: string[];
  /** Quantidade por função quando difere da padrão (0 = fora do evento). */
  need: Record<string, number>;
}

function withoutSkipped({ skipped: _skipped, ...rule }: RecurrenceRule): RecurrenceRule {
  return rule;
}

export function createEvents(
  events: ScheduleEvent[],
  draft: EventDraft,
  ministries: Ministry[],
  idSeed: string
): { events: ScheduleEvent[]; created: ScheduleEvent[] } {
  // Série nova não herda as datas excluídas de outra (o "Duplicar" copia a regra).
  const rule = draft.recurrence ? withoutSkipped({ ...draft.recurrence, start: draft.date }) : null;
  const dates = rule ? seriesDates(rule) : [draft.date];

  const need: Record<string, number> = {};

  for (const ministryId of draft.ministryIds) {
    for (const role of ministries.find((m) => m.id === ministryId)?.roles ?? []) {
      if (draft.need[role.id] !== undefined && draft.need[role.id] !== role.qty)
        need[role.id] = draft.need[role.id]!;
    }
  }

  const seriesId = `s${idSeed}`;
  const created = dates.map((date): ScheduleEvent => ({
    id: `${seriesId}-${date}`,
    seriesId,
    title: draft.title.trim(),
    date,
    time: draft.time,
    recurrence: rule ? recurrenceLabel(rule) : null,
    rule,
    color: draft.color ?? null,
    ministryIds: [...draft.ministryIds],
    need: { ...need },
    slots: {},
    generated: false,
    published: false,
    edited: false,
    responses: {},
    attendance: {},
    nudgedAt: null,
    recorded: false,
    notices: {},
    swaps: [],
    audience: null,
  }));

  return { events: [...events, ...created].sort(byDateTime), created };
}

/**
 * Aplica a edição a uma data ou à série, sem apagar a história: as datas que já aconteceram
 * com quem serviu marcado nunca perdem ninguém (ver `eventEdit`). As datas futuras com
 * escala e as passadas sem registro só recebem a edição se a pessoa escolher.
 */
export function updateEvents(
  events: ScheduleEvent[],
  draft: EventDraft,
  ministries: Ministry[],
  options: EditOptions
): ScheduleEvent[] {
  const edited = applyEventEdit(events, draft, ministries, options).sort(byDateTime);
  const series = draft.isRecurring && draft.scope !== 'one' && draft.seriesId;

  // O término depois da edição: as datas novas nascem com a configuração editada.
  return draft.until && series ? changeSeriesEnd(edited, series, draft.until, options.today) : edited;
}

/** As datas que a exclusão leva: esta, esta e as próximas da série, ou a série toda. */
export function eventsInScope(
  events: readonly ScheduleEvent[],
  event: ScheduleEvent,
  scope: EventScope
): ScheduleEvent[] {
  if (scope === 'one') return events.filter((e) => e.id === event.id);

  return events.filter(
    (e) => e.seriesId === event.seriesId && (scope === 'series' || e.date + e.time >= event.date + event.time)
  );
}

/**
 * Exclui esta data, esta e as próximas (encerra a série a partir dela, guardando o que já
 * passou) ou a série toda. É a exclusão simples — também a do "Desfazer" de uma criação;
 * "Só esta data" numa série que se repete é `skipOccurrence` (`skippedDates`), que guarda
 * a data na regra para ela não voltar.
 */
export function deleteEvents(
  events: ScheduleEvent[],
  event: ScheduleEvent,
  scope: EventScope
): ScheduleEvent[] {
  const gone = new Set(eventsInScope(events, event, scope).map((e) => e.id));

  return events.filter((e) => !gone.has(e.id));
}

export interface MinistryDraft {
  id: string | null;
  name: string;
  description: string;
  color: string;
  icon: MinistryIconKey;
  leaderIds: string[];
  /** Ministério de acolhimento (gerencia os visitantes); ausente = mantém o que está gravado. */
  welcome?: boolean;
}

export function validateMinistry(ministries: Ministry[], draft: MinistryDraft): string | null {
  const name = draft.name.trim();

  if (!name) return 'Informe o nome do ministério.';
  if (ministries.some((m) => m.id !== draft.id && normalize(m.name) === normalize(name))) {
    return 'Já existe um ministério com esse nome.';
  }

  return null;
}

/**
 * Cria ou edita o ministério. Na criação, `roles` são as funções que já nascem com ele
 * (as de um modelo — `ministryTemplates`); na edição, as funções não mudam.
 */
export function saveMinistry(
  ministries: Ministry[],
  draft: MinistryDraft,
  newId: string,
  roles: Role[] = []
): Ministry[] {
  const fields = {
    name: draft.name.trim(),
    description: draft.description.trim(),
    color: draft.color,
    icon: draft.icon,
    leaderIds: [...new Set(draft.leaderIds)],
  };

  if (draft.id) {
    return ministries.map((m) =>
      m.id === draft.id ? { ...m, ...fields, welcome: draft.welcome ?? m.welcome } : m
    );
  }

  return [...ministries, { id: newId, roles, ...fields, welcome: draft.welcome ?? false }];
}

/** Remove o ministério, suas funções dos eventos e a habilitação dos membros. */
export function deleteMinistry(state: DomainState, ministryId: string): DomainState {
  const roleIds = state.ministries.find((m) => m.id === ministryId)?.roles.map((r) => r.id) ?? [];

  return {
    ...state,
    ministries: state.ministries.filter((m) => m.id !== ministryId),
    events: state.events.map((e) => {
      if (!e.ministryIds.includes(ministryId)) return e;

      const slots = { ...e.slots };
      roleIds.forEach((id) => delete slots[id]);

      return syncTracking({ ...e, ministryIds: e.ministryIds.filter((id) => id !== ministryId), slots });
    }),
    members: state.members.map((m) => {
      if (!m.roles[ministryId]) return m;

      const roles = { ...m.roles };
      delete roles[ministryId];

      return { ...m, roles };
    }),
  };
}

export function addRole(ministries: Ministry[], ministryId: string, roleId: string): Ministry[] {
  const role: Role = {
    id: roleId,
    name: 'Nova função',
    qty: 1,
    allowedSex: 'any',
    rules: [],
    leaderIds: [],
    icon: 'user',
    ...NO_ROLE_RULES,
  };

  return ministries.map((m) => (m.id === ministryId ? { ...m, roles: [...m.roles, role] } : m));
}

export function updateRole(
  ministries: Ministry[],
  ministryId: string,
  roleId: string,
  patch: Partial<Omit<Role, 'id'>>
) {
  return ministries.map((m) =>
    m.id === ministryId ? { ...m, roles: m.roles.map((r) => (r.id === roleId ? { ...r, ...patch } : r)) } : m
  );
}

export function deleteRole(state: DomainState, ministryId: string, roleId: string): DomainState {
  return {
    ...state,
    ministries: state.ministries.map((m) =>
      m.id === ministryId ? { ...m, roles: m.roles.filter((r) => r.id !== roleId) } : m
    ),
    events: state.events.map((e) => {
      if (!(roleId in e.slots)) return e;

      const slots = { ...e.slots };
      delete slots[roleId];

      return syncTracking({ ...e, slots });
    }),
    members: state.members.map((m) => {
      const current = m.roles[ministryId];

      if (!current?.includes(roleId)) return m;

      const roles = { ...m.roles, [ministryId]: current.filter((id) => id !== roleId) };

      if (!roles[ministryId]!.length) delete roles[ministryId];

      return { ...m, roles };
    }),
  };
}

/** Habilita o membro numa função (atalho de Ministérios › "Adicionar"). */
export function enableInRole(
  members: Member[],
  memberId: string,
  ministryId: string,
  roleId: string
): Member[] {
  return members.map((m) => {
    const current = m.roles[ministryId] ?? [];

    if (m.id !== memberId || current.includes(roleId)) return m;

    return { ...m, roles: { ...m.roles, [ministryId]: [...current, roleId] } };
  });
}

export interface RoleRemovalImpact {
  /** Escalas de hoje em diante em que a pessoa está nesta função, em ordem de data. */
  events: ScheduleEvent[];
  /** Quantas já estavam publicadas (a vaga reabre e a pessoa é avisada). */
  published: number;
  /** Era Líder de Função. */
  leader: boolean;
  /** Estava em treinamento na função. */
  trainee: boolean;
  /** Pares restritos desfeitos (a outra pessoa de cada par). */
  pairs: number;
  /** Pares que servem juntos desfeitos. */
  togetherPairs: number;
}

/** O que tirar o membro da função muda — o diálogo mostra antes de confirmar. */
export function roleRemovalImpact(
  state: Pick<DomainState, 'events' | 'ministries'>,
  memberId: string,
  roleId: string,
  today: string
): RoleRemovalImpact {
  const role = state.ministries.flatMap((m) => m.roles).find((r) => r.id === roleId);
  const events = state.events
    .filter((e) => stillAhead(e, today) && (e.slots[roleId] ?? []).includes(memberId))
    .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));

  return {
    events,
    published: events.filter((e) => e.published).length,
    leader: !!role?.leaderIds.includes(memberId),
    trainee: !!role?.trainees.includes(memberId),
    pairs: role?.blockedPairs.filter((pair) => pair.includes(memberId)).length ?? 0,
    togetherPairs: role?.togetherPairs.filter((pair) => pair.includes(memberId)).length ?? 0,
  };
}

/**
 * Tira o membro da função (Ministérios › "×" no chip): deixa de estar habilitado, de liderar,
 * de estar em treinamento e de ter par (restrito ou que serve junto) nela, e sai das escalas de hoje em diante
 * (a publicada continua publicada; quem saiu é avisado).
 */
export function disableInRole(
  state: Pick<DomainState, 'members' | 'ministries' | 'events'>,
  memberId: string,
  ministryId: string,
  roleId: string,
  today: string
): Pick<DomainState, 'members' | 'ministries' | 'events'> {
  const affected = new Set(roleRemovalImpact(state, memberId, roleId, today).events.map((e) => e.id));

  return {
    members: state.members.map((m) => {
      const current = m.roles[ministryId] ?? [];

      if (m.id !== memberId || !current.includes(roleId)) return m;

      const { [ministryId]: _, ...others } = m.roles;
      const next = current.filter((id) => id !== roleId);

      return { ...m, roles: next.length ? { ...others, [ministryId]: next } : others };
    }),
    ministries: state.ministries.map((ministry) =>
      ministry.id === ministryId
        ? {
            ...ministry,
            roles: ministry.roles.map((role) =>
              role.id === roleId
                ? {
                    ...role,
                    leaderIds: role.leaderIds.filter((id) => id !== memberId),
                    trainees: role.trainees.filter((id) => id !== memberId),
                    blockedPairs: role.blockedPairs.filter((pair) => !pair.includes(memberId)),
                    togetherPairs: role.togetherPairs.filter((pair) => !pair.includes(memberId)),
                  }
                : role
            ),
          }
        : ministry
    ),
    events: [...affected].reduce((events, id) => removeMember(events, id, roleId, memberId), state.events),
  };
}

/**
 * O Líder de Função precisa estar habilitado nela: quem deixa de estar (ou some do
 * cadastro) perde a liderança; os outros líderes da função continuam.
 */
export function releaseRoleLeaders(ministries: Ministry[], members: Member[]): Ministry[] {
  const enabled = (memberId: string, ministryId: string, roleId: string) =>
    members.some((m) => m.id === memberId && (m.roles[ministryId] ?? []).includes(roleId));
  let changed = false;
  const next = ministries.map((m) => {
    if (!m.roles.some((r) => r.leaderIds.some((id) => !enabled(id, m.id, r.id)))) return m;

    changed = true;

    return {
      ...m,
      roles: m.roles.map((r) =>
        r.leaderIds.some((id) => !enabled(id, m.id, r.id))
          ? { ...r, leaderIds: r.leaderIds.filter((id) => enabled(id, m.id, r.id)) }
          : r
      ),
    };
  });

  return changed ? next : ministries;
}

export type MemberPatch = Partial<
  Pick<
    Member,
    | 'name'
    | 'nickname'
    | 'email'
    | 'phone'
    | 'birthDate'
    | 'sex'
    | 'spouseId'
    | 'spouseText'
    | 'coupleMode'
    | 'roles'
    | 'civilStatus'
    | 'profession'
    | 'churchStatus'
    | 'memberSince'
    | 'joinedVia'
    | 'baptismDate'
    | 'originChurch'
    | 'cpf'
    | 'address'
    | 'emergency'
  >
>;

/**
 * Atualiza o membro. O vínculo de cônjuge é recíproco: vincular A a B desfaz o vínculo
 * anterior de A e o de B, e limpa o nome digitado em B. A escala do casal (`coupleMode`)
 * também: vale para os dois, recomeça sem preferência num vínculo novo (a não ser que o
 * patch traga a escolha) e some de quem fica sem cônjuge.
 */
export function updateMember(members: Member[], memberId: string, patch: MemberPatch): Member[] {
  const before = members.find((m) => m.id === memberId);
  let next = members.map((m) => (m.id === memberId ? { ...m, ...patch } : m));
  const previous = before?.spouseId ?? null;
  const relinked = 'spouseId' in patch && (patch.spouseId ?? null) !== previous;

  if (relinked) {
    const target = patch.spouseId ?? null;
    const targetsPrevious = target ? (members.find((m) => m.id === target)?.spouseId ?? null) : null;
    const mode = target ? (patch.coupleMode ?? null) : null;

    next = next.map((m) => {
      if (m.id === memberId) return { ...m, coupleMode: mode };
      if (target && m.id === target) return { ...m, spouseId: memberId, spouseText: '', coupleMode: mode };
      if (previous && m.id === previous && previous !== target)
        return { ...m, spouseId: null, coupleMode: null };
      if (targetsPrevious && m.id === targetsPrevious) return { ...m, spouseId: null, coupleMode: null };

      return m;
    });
  } else if ('coupleMode' in patch) {
    const mode = previous ? (patch.coupleMode ?? null) : null;

    next = next.map((m) => {
      if (m.id === memberId) return { ...m, coupleMode: mode };
      if (previous && m.id === previous) return { ...m, coupleMode: mode };

      return m;
    });
  }

  return next;
}

/**
 * Grava o cadastro inteiro de uma vez — insere o membro novo ou substitui o existente —,
 * como o perfil faz ao clicar em Salvar. O cônjuge e a escala do casal passam por
 * `updateMember`, para o vínculo e a escolha continuarem recíprocos.
 */
export function saveMember(members: Member[], member: Member): Member[] {
  const saved = members.find((m) => m.id === member.id);
  const previousSpouse = saved?.spouseId ?? null;
  const previousMode = saved?.coupleMode ?? null;
  // Endereço novo perde a coordenada do anterior (o servidor busca a nova).
  const located = keepLocation(member);
  const next = saved
    ? members.map((m) =>
        m.id === member.id ? { ...located, spouseId: previousSpouse, coupleMode: previousMode } : m
      )
    : [...members, { ...located, spouseId: null, coupleMode: null }];
  const patch: MemberPatch = {};

  if (member.spouseId !== previousSpouse) patch.spouseId = member.spouseId;
  if (member.coupleMode !== previousMode || 'spouseId' in patch) patch.coupleMode = member.coupleMode;

  return Object.keys(patch).length ? updateMember(next, member.id, patch) : next;
}

export function newMember(
  id: string,
  fields: { name: string; birthDate: string | null; sex: Sex; roles?: Record<string, string[]> }
): Member {
  return {
    id,
    inactiveSince: null,
    selfUpdatedAt: null,
    admin: false,
    photo: null,
    name: fields.name.trim(),
    nickname: '',
    email: '',
    phone: '',
    birthDate: fields.birthDate,
    sex: fields.sex,
    spouseId: null,
    spouseText: '',
    coupleMode: null,
    roles: fields.roles ?? {},
    availability: Array<AvailState>(21).fill('unset'),
    blackouts: [],
    ...EMPTY_PROFILE,
  };
}

/** As linhas válidas viram membros, sem funções nem disponibilidade (configuradas no Escala). */
export function importMembers(members: Member[], rows: ImportRow[], idSeed: string): Member[] {
  const added = rows
    // Quem já está cadastrado (`linkTo`) não é criado de novo: a linha só completa o cadastro.
    .filter((r) => r.ok && !r.linkTo && r.member.sex)
    .map((r, i): Member => {
      const { sex, ...fields } = r.member;
      const base = newMember(`i${idSeed}${i}`, { name: fields.name, birthDate: fields.birthDate, sex: sex! });

      return { ...base, ...fields, name: base.name };
    });

  return [...members, ...added];
}

// ===== Confirmação de presença, check-in e cobrança =====

/**
 * Resposta do membro à escala. Confirmar só registra; "Não posso ir" tira a pessoa da
 * função e deixa a recusa (com o motivo) como vaga reaberta. Nenhuma das duas tira o
 * evento de "publicado": a escala dos outros continua valendo.
 */
export function respond(
  events: ScheduleEvent[],
  eventId: string,
  memberId: string,
  response: { status: ResponseStatus; reason?: string; at: number }
): ScheduleEvent[] {
  return events.map((e) => {
    if (e.id !== eventId) return e;

    const roleId = Object.entries(e.slots).find(([, ids]) => ids.includes(memberId))?.[0];

    if (!roleId) return e;

    const entry = {
      status: response.status,
      roleId,
      reason: (response.reason ?? '').trim(),
      at: response.at,
    };

    if (response.status === 'confirmed') {
      return { ...e, responses: { ...e.responses, [memberId]: { ...entry, reason: '' } } };
    }

    return syncTracking({
      ...e,
      slots: { ...e.slots, [roleId]: (e.slots[roleId] ?? []).filter((id) => id !== memberId) },
      responses: { ...e.responses, [memberId]: entry },
    });
  });
}

/**
 * Grava o check-in das funções `roleIds` (as que o líder edita): quem está nelas fica
 * com a marcação enviada (ou sem marcação); as demais funções não mudam.
 */
export function recordAttendance(
  events: ScheduleEvent[],
  eventId: string,
  marks: Record<string, Attendance>,
  roleIds: readonly string[] | null = null
): ScheduleEvent[] {
  return events.map((e) => {
    if (e.id !== eventId) return e;

    const inScope = new Set(
      Object.entries(e.slots)
        .filter(([roleId]) => !roleIds || roleIds.includes(roleId))
        .flatMap(([, ids]) => ids)
    );
    const attendance = Object.fromEntries(Object.entries(e.attendance).filter(([id]) => !inScope.has(id)));

    for (const [memberId, mark] of Object.entries(marks)) {
      if (!inScope.has(memberId)) continue;
      attendance[memberId] = {
        status: mark.status,
        substituteId: mark.status === 'absent' && mark.substituteId !== memberId ? mark.substituteId : null,
      };
    }

    return { ...e, attendance };
  });
}

/**
 * Desfaz a resposta do membro: volta à de antes (`previous`, ou pendente com `null`). Se
 * ele tinha avisado que não pode ir, volta à função da recusa — que deixa de ser vaga
 * reaberta. Sem a recusa (a vaga já foi preenchida), nada muda: confira com `canRetract`.
 */
export function retractResponse(
  events: ScheduleEvent[],
  eventId: string,
  memberId: string,
  previous: ScheduleResponse | null
): ScheduleEvent[] {
  return events.map((e) => {
    const current = e.responses[memberId];

    if (e.id !== eventId || !current) return e;

    const responses = { ...e.responses };

    if (previous) responses[memberId] = previous;
    else delete responses[memberId];

    if (current.status === 'confirmed') return { ...e, responses };

    const placed = Object.values(e.slots).some((ids) => ids.includes(memberId));

    if (placed) return e;

    return {
      ...e,
      slots: { ...e.slots, [current.roleId]: [...(e.slots[current.roleId] ?? []), memberId] },
      responses,
    };
  });
}

// ===== Pedido de troca =====

function updateSwap(
  events: ScheduleEvent[],
  eventId: string,
  swapId: string,
  change: (event: ScheduleEvent, swap: SwapRequest) => ScheduleEvent
): ScheduleEvent[] {
  return events.map((e) => {
    const swap = e.id === eventId ? e.swaps.find((s) => s.id === swapId) : undefined;

    return swap ? change(e, swap) : e;
  });
}

const withSwap = (event: ScheduleEvent, swapId: string, patch: Partial<SwapRequest>): ScheduleEvent => ({
  ...event,
  swaps: event.swaps.map((s) => (s.id === swapId ? { ...s, ...patch } : s)),
});

/**
 * "Pedir troca": a pessoa escalada pede a um colega que assuma a sua função. Continua
 * escalada até o líder aprovar. Confira antes com `swapRequestError`.
 */
export function requestSwap(
  events: ScheduleEvent[],
  eventId: string,
  request: { id: string; fromId: string; toId: string; at: number }
): ScheduleEvent[] {
  return events.map((e) => {
    if (e.id !== eventId) return e;

    const roleId = Object.entries(e.slots).find(([, ids]) => ids.includes(request.fromId))?.[0];

    if (!roleId) return e;

    return { ...e, swaps: [...e.swaps, { ...request, roleId, status: 'peer' }] };
  });
}

/** Quem pediu desiste do pedido em aberto. */
export function cancelSwap(events: ScheduleEvent[], eventId: string, swapId: string, at: number) {
  return updateSwap(events, eventId, swapId, (e, s) =>
    isOpenSwap(s) ? withSwap(e, swapId, { status: 'canceled', at }) : e
  );
}

/** O colega aceita (o pedido vai ao líder) ou recusa. */
export function answerSwap(
  events: ScheduleEvent[],
  eventId: string,
  swapId: string,
  accept: boolean,
  at: number
): ScheduleEvent[] {
  return updateSwap(events, eventId, swapId, (e, s) =>
    s.status === 'peer' ? withSwap(e, swapId, { status: accept ? 'leader' : 'declined', at }) : e
  );
}

/**
 * O líder da função decide. Aprovar troca as pessoas na mesma posição da função: quem
 * pediu sai (sem resposta nem aviso — foi ele que pediu) e o colega entra já confirmado.
 * Se a escala mudou desde o pedido (`swapApprovalError`), o pedido é cancelado.
 */
export function decideSwap(
  events: ScheduleEvent[],
  eventId: string,
  swapId: string,
  approve: boolean,
  lookup: Lookup,
  at: number
): ScheduleEvent[] {
  return updateSwap(events, eventId, swapId, (e, s) => {
    if (s.status !== 'leader') return e;
    if (!approve) return withSwap(e, swapId, { status: 'rejected', at });
    if (swapApprovalError(e, s, lookup)) return withSwap(e, swapId, { status: 'canceled', at });

    const responses = { ...e.responses };

    delete responses[s.fromId];
    responses[s.toId] = { status: 'confirmed', roleId: s.roleId, reason: '', at };

    const notices = { ...e.notices };

    delete notices[s.fromId];
    delete notices[s.toId];

    return syncTracking(
      withSwap(
        {
          ...e,
          slots: {
            ...e.slots,
            [s.roleId]: (e.slots[s.roleId] ?? []).map((id) => (id === s.fromId ? s.toId : id)),
          },
          responses,
          notices,
        },
        swapId,
        { status: 'approved', at }
      )
    );
  });
}

export function markNudged(events: ScheduleEvent[], eventId: string, at: number): ScheduleEvent[] {
  return events.map((e) => (e.id === eventId ? { ...e, nudgedAt: at } : e));
}

/** Registra (ou edita) o público do culto: as contagens agregadas, com quem e quando. */
export function recordAudience(
  events: ScheduleEvent[],
  eventId: string,
  input: AudienceInput,
  by: string,
  at: number
): ScheduleEvent[] {
  return events.map((e) => (e.id === eventId ? withAudience(e, input, by, at) : e));
}

/**
 * "Datas em que não posso": marca a data como ausência de um dia ou, se já estava
 * marcada assim, desmarca. Ausências mais longas (férias) ficam com o perfil.
 */
export function toggleUnavailableDate(member: Member, date: string, blackoutId: string): Member {
  const quick = member.blackouts.find((b) => b.from === date && b.to === date);

  if (quick) return { ...member, blackouts: member.blackouts.filter((b) => b.id !== quick.id) };

  return {
    ...member,
    blackouts: [
      ...member.blackouts,
      { id: blackoutId, from: date, to: date, reason: QUICK_BLACKOUT_REASON },
    ].sort((a, b) => a.from.localeCompare(b.from)),
  };
}

// ===== Cadastro público e aprovação =====

/** Cadastro recebido pelo link público: entra como solicitação pendente. */
export function submitRegistration(
  registrations: Registration[],
  id: string,
  data: RegistrationData,
  at: number,
  photo: string | null = null
): Registration[] {
  return [
    ...registrations,
    {
      id,
      data,
      status: 'pending',
      submittedAt: at,
      decidedAt: null,
      decidedBy: '',
      reason: '',
      memberId: null,
      linked: false,
      mergedFields: [],
      photo,
    },
  ];
}

/**
 * "Aprovar e incluir": cria o membro com os dados enviados (e a foto, se veio) e marca a
 * solicitação como aprovada. Confira antes com `approvalError` (nome e e-mail únicos).
 * Decidida, a solicitação não guarda mais a foto: ela passou ao membro, ou foi descartada.
 */
export function approveRegistration(
  state: Pick<DomainState, 'members' | 'registrations'>,
  registrationId: string,
  memberId: string,
  who: string,
  at: number
): Pick<DomainState, 'members' | 'registrations'> {
  const registration = state.registrations.find((r) => r.id === registrationId);

  if (!registration || registration.status !== 'pending') return state;

  return {
    members: [...state.members, memberFromRegistration(registration, memberId)],
    registrations: state.registrations.map((r) =>
      r.id === registrationId
        ? { ...r, status: 'approved', decidedAt: at, decidedBy: who, reason: '', memberId, photo: null }
        : r
    ),
  };
}

export function mergeRegistration(
  state: Pick<DomainState, 'members' | 'registrations'>,
  registrationId: string,
  memberId: string,
  fields: readonly MergeField[],
  who: string,
  at: number
): Pick<DomainState, 'members' | 'registrations'> {
  const registration = state.registrations.find((r) => r.id === registrationId);
  const member = state.members.find((m) => m.id === memberId);

  if (!registration || registration.status !== 'pending' || !member) return state;

  return {
    members: saveMember(state.members, applyMerge(member, registration, fields)),
    registrations: state.registrations.map((r) =>
      r.id === registrationId
        ? {
            ...r,
            status: 'approved',
            decidedAt: at,
            decidedBy: who,
            reason: '',
            memberId,
            linked: true,
            mergedFields: [...fields],
            photo: null,
          }
        : r
    ),
  };
}

/** Recusa a solicitação, com o motivo (opcional); fica no Histórico, sem a foto. */
export function rejectRegistration(
  registrations: Registration[],
  registrationId: string,
  reason: string,
  who: string,
  at: number
): Registration[] {
  return registrations.map((r) =>
    r.id === registrationId && r.status === 'pending'
      ? { ...r, status: 'rejected', decidedAt: at, decidedBy: who, reason: reason.trim(), photo: null }
      : r
  );
}

/** "Gerar novo link": o anterior para de funcionar; o novo já nasce recebendo cadastros. */
export function rotateRegistrationLink(token: string, at: number): RegistrationLink {
  return { token, active: true, createdAt: at };
}

/** Pausa ou retoma os cadastros pelo link (o endereço continua o mesmo). */
export function setRegistrationLinkActive(link: RegistrationLink, active: boolean): RegistrationLink {
  return { ...link, active };
}
